import React, { Suspense, useState, useCallback, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import BottomNav from './BottomNav';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import SkeletonLoader from '../backoffice/SkeletonLoader';
import InactivityGuard from '../session/InactivityGuard';

const SIDEBAR_KEY = 'sidebar_collapsed';

const AppShell: React.FC = () => {
  const { isMobile, isTablet } = useBreakpoint();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Bloqueo del scroll de fondo mientras el offcanvas está abierto: evita que la
  // página detrás se desplace y que el menú "se pierda" al hacer scroll.
  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobile, mobileMenuOpen]);

  // Cerrar el menú si dejamos de estar en móvil (evita quedar bloqueado en desktop).
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) setMobileMenuOpen(false);
  }, [isMobile, mobileMenuOpen]);

  const showSidebar = !isMobile;
  const sidebarWidth = collapsed ? 'ml-16' : 'ml-60';

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-secondary to-surface-tertiary">
      {/* Cierre de sesión por inactividad (solo usuarios autenticados). */}
      <InactivityGuard />

      {/* Desktop/Tablet sidebar */}
      {showSidebar && <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} />}

      {/* Mobile sidebar overlay — por encima del bottom nav (z-40) para no chocar */}
      {isMobile && mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeMobileMenu} />
          <div className="fixed left-0 top-0 h-full z-50">
            <Sidebar collapsed={false} onToggle={closeMobileMenu} onNavigate={closeMobileMenu} />
          </div>
        </>
      )}

      {/* Main content area */}
      <div className={`flex flex-col min-h-screen transition-[margin] duration-200 ${showSidebar ? sidebarWidth : ''}`}>
        <TopBar onMenuToggle={toggleMobileMenu} showMenuButton={isMobile || isTablet} />

        <main className={`flex-1 ${isMobile ? 'pb-20' : ''}`}>
          <Suspense fallback={(
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <SkeletonLoader type="page" />
            </div>
          )}
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile bottom nav — oculto mientras el offcanvas está abierto para no solaparse */}
      {isMobile && !mobileMenuOpen && <BottomNav onMorePress={toggleMobileMenu} />}
    </div>
  );
};

export default AppShell;
